import React, { useState, useMemo } from 'react';
import { Music, Instagram } from 'lucide-react';
import { songs, titles, genres, artists, languages } from './data/songs';
import { SearchBar } from './components/SearchBar';
import { Filters } from './components/Filters';
import { SongList } from './components/SongList';
import type { Song, SongFilter } from './types';

function App() {
  const [filters, setFilters] = useState<SongFilter>({
    search: '',
    title: '',
    genre: '',
    artist: '',
    language: '',
  });

  const filteredSongs = useMemo(() => {
    let result = [...songs]; // Create a copy to avoid mutating the original array
    
    // Si un filtre est actif, on l'applique exclusivement
    if (filters.title) {
      result = result.filter(song => song.title === filters.title);
    } else if (filters.genre) {
      result = result.filter(song => song.genre === filters.genre);
    } else if (filters.artist) {
      result = result.filter(song => song.artist === filters.artist);
    } else if (filters.language) {
      result = result.filter(song => song.language === filters.language);
    } else if (filters.search) {
      // Si aucun filtre n'est actif, on applique la recherche
      result = result.filter(song => 
        song.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        song.artist.toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    
    // Toujours trier par ordre alphabétique du titre
    return result.sort((a, b) => a.title.localeCompare(b.title));
  }, [filters]);

  const handleSongSelect = (song: Song) => {
    // TODO: Implement song selection and registration
    console.log('Selected song:', song);
  };

  // Réinitialiser tous les autres filtres lorsqu'un filtre est sélectionné
  const handleTitleChange = (title: string) => {
    setFilters({ search: '', title, genre: '', artist: '', language: '' });
  };

  const handleGenreChange = (genre: string) => {
    setFilters({ search: '', title: '', genre, artist: '', language: '' });
  };

  const handleArtistChange = (artist: string) => {
    setFilters({ search: '', title: '', genre: '', artist, language: '' });
  };

  const handleLanguageChange = (language: string) => {
    setFilters({ search: '', title: '', genre: '', artist: '', language: '' });
  };

  const handleSearchChange = (search: string) => {
    setFilters({ search, title: '', genre: '', artist: '', language: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="relative h-48 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1200&q=80"
            alt="Bannière musicale"
            className="w-full h-full object-cover object-center brightness-50"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="flex flex-col items-center justify-center gap-2">
                <div className="w-16 h-16 mb-1">
                  <Music className="h-16 w-16 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white">AdventChill Karaoké</h1>
              </div>
              <p className="text-lg text-white mt-1">Soirée Gospel & Louange</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow">
        <div className="space-y-6">
          {/* Welcome message */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-purple-100">
            <h2 className="text-xl font-semibold text-purple-800 mb-2">Bienvenue à notre soirée karaoké Gospel & Louange ! 🎤✨</h2>
            <p className="text-gray-700 mb-3">
              Prêt(e) à faire vibrer la salle ? Que vous soyez un pro du micro ou un chanteur de salle de bain, 
              c'est le moment de briller ! Parcourez notre sélection de chansons, choisissez votre titre préféré, 
              remplissez votre carton de participation et rejoignez-nous pour un moment de partage et de joie.
            </p>
            <p className="text-gray-700 font-medium">
              Pas d'inquiétude, ici pas de jury ni de notes - juste du plaisir, de la bonne humeur et des 
              encouragements. Alors lancez-vous, la scène n'attend que vous ! 🙌
            </p>
          </div>

          {/* Search and filters */}
          <div className="space-y-4">
            <SearchBar
              value={filters.search}
              onChange={handleSearchChange}
            />
            <Filters
              titles={titles}
              genres={genres}
              artists={artists}
              languages={languages}
              selectedTitle={filters.title}
              selectedGenre={filters.genre}
              selectedArtist={filters.artist}
              selectedLanguage={filters.language}
              onTitleChange={handleTitleChange}
              onGenreChange={handleGenreChange}
              onArtistChange={handleArtistChange}
              onLanguageChange={handleLanguageChange}
            />
          </div>

          {/* Results */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {filteredSongs.length} chansons disponibles
            </h2>
            <SongList songs={filteredSongs} onSongSelect={handleSongSelect} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center">
            <div className="w-16 h-16 mb-3">
              <img 
                src="https://i.imgur.com/Rl5JnhI.png" 
                alt="AdventChill Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <a 
                href="https://www.instagram.com/advent_chill/following/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-purple-600 hover:text-purple-800 transition-colors"
              >
                <Instagram className="h-5 w-5" />
                <span>Suivez-nous sur Instagram</span>
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2025 AdventChill. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;